import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight, Search, Tag } from 'lucide-react';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'Tümü', count: 12 },
    { id: 'mobile', name: 'Mobil Uygulama', count: 4 },
    { id: 'ai', name: 'Yapay Zeka', count: 3 },
    { id: 'web', name: 'Web Geliştirme', count: 3 },
    { id: 'marketing', name: 'Dijital Pazarlama', count: 2 }
  ];

  const featuredPost = {
    id: 1,
    slug: 'yapay-zeka-ile-mobil-uygulama-gelistirme',
    title: '2025\'te Yapay Zeka ile Mobil Uygulama Geliştirme: Kapsamlı Rehber',
    excerpt: 'Yapay zeka teknolojilerinin mobil uygulama geliştirme süreçlerini nasıl dönüştürdüğünü ve bu teknolojileri projelerinizde nasıl kullanabileceğinizi keşfedin.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
    category: 'ai',
    author: 'Elif Demir',
    date: '10 Aralık 2025',
    readTime: '12 dk'
  };

  const posts = [
    {
      id: 2,
      slug: 'react-native-vs-flutter-2025',
      title: 'React Native vs Flutter: 2025 Karşılaştırması',
      excerpt: 'İki popüler cross-platform framework\'ün detaylı karşılaştırması ve projeniz için hangisini seçmeniz gerektiği.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop',
      category: 'mobile',
      author: 'Mehmet Kaya',
      date: '8 Aralık 2025',
      readTime: '8 dk'
    },
    {
      id: 3,
      slug: 'seo-trendleri-2025',
      title: 'SEO Trendleri 2025: Arama Motorlarında Üst Sıralara Çıkın',
      excerpt: 'Google\'ın son algoritma güncellemeleri ve web sitenizi üst sıralara taşıyacak SEO stratejileri.',
      image: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=600&h=400&fit=crop',
      category: 'marketing',
      author: 'Zeynep Arslan',
      date: '5 Aralık 2025',
      readTime: '6 dk'
    },
    {
      id: 4,
      slug: 'next-js-14-yenilikleri',
      title: 'Next.js 14 ile Modern Web Uygulamaları',
      excerpt: 'Next.js 14\'ün getirdiği yenilikler ve performans iyileştirmeleri ile web projelerinizi nasıl güçlendirirsiniz.',
      image: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=600&h=400&fit=crop',
      category: 'web',
      author: 'Ahmet Yılmaz',
      date: '3 Aralık 2025',
      readTime: '10 dk'
    },
    {
      id: 5,
      slug: 'chatgpt-api-entegrasyonu',
      title: 'ChatGPT API\'yi Uygulamanıza Nasıl Entegre Edersiniz?',
      excerpt: 'Adım adım ChatGPT API entegrasyonu ve yapay zeka destekli özellikler geliştirme rehberi.',
      image: 'https://images.unsplash.com/photo-1676573409967-986dcf64d35a?w=600&h=400&fit=crop',
      category: 'ai',
      author: 'Elif Demir',
      date: '1 Aralık 2025',
      readTime: '15 dk'
    },
    {
      id: 6,
      slug: 'mobil-uygulama-monetizasyon',
      title: 'Mobil Uygulama Monetizasyon Stratejileri',
      excerpt: 'Uygulamanızdan gelir elde etmenin en etkili yolları: In-app satın almalar, reklamlar ve abonelik modelleri.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop',
      category: 'mobile',
      author: 'Mehmet Kaya',
      date: '28 Kasım 2025',
      readTime: '7 dk'
    }
  ];

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              RUYAN
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Blog</span>
            </h1>

            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              Teknoloji dünyasından en güncel içerikler, trendler ve pratik rehberler
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Blog yazılarında ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-800/50 border-slate-700 text-white pl-12 pr-4 py-6 rounded-xl focus:border-cyan-500"
                data-testid="blog-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white'
                    : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50'
                }`}
                data-testid={`blog-category-${category.id}`}
              >
                {category.name}
                <span className="ml-2 text-xs opacity-70">({category.count})</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {activeCategory === 'all' && !searchTerm && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to={`/blog/${featuredPost.slug}`} className="block group">
              <div className="relative overflow-hidden rounded-3xl bg-slate-800/40 border border-slate-700/50 hover:border-cyan-500/50 transition-all">
                <div className="grid lg:grid-cols-2">
                  <div className="relative h-64 lg:h-96 overflow-hidden">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-cyan-500 text-white text-xs font-medium rounded-full">
                        Öne Çıkan
                      </span>
                    </div>
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-slate-700/50 text-cyan-400 text-xs font-medium rounded-full">
                        {getCategoryName(featuredPost.category)}
                      </span>
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-gray-400 mb-6 line-clamp-3">{featuredPost.excerpt}</p>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {featuredPost.author}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {featuredPost.date}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {featuredPost.readTime}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all hover:-translate-y-2"
                data-testid={`blog-post-${post.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-slate-900/80 text-cyan-400 text-xs font-medium rounded-full">
                      {getCategoryName(post.category)}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {post.readTime}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">Aramanızla eşleşen blog yazısı bulunamadı.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-3xl p-8 md:p-12 text-center">
            <Tag className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Bültenimize Abone Olun</h2>
            <p className="text-gray-400 mb-8">
              Haftalık teknoloji güncellemeleri ve özel içerikler için e-posta listemize katılın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="E-posta adresiniz"
                className="bg-slate-900/50 border-slate-700 text-white h-12 rounded-xl focus:border-cyan-500"
              />
              <button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-6 h-12 rounded-xl font-medium transition-all hover:scale-105 flex items-center justify-center gap-2">
                Abone Ol
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
